import {ID} from '@wandb/weave/core';
import {getType} from 'typesafe-actions';

import {SectionPanelSorting} from '../../components/PanelBank/types';
import {recordRumAction} from '../../util/datadog/datadogRum';
import {panelSortingKeyFromPanel} from '../../util/panelbank';
import {type PanelConfig} from '../../util/panelTypes';
import {isSparseOverridesEnabled} from '../../util/sparseOverridesFeatureFlag';
import {setInShallowClone} from '../../util/utility';
import * as ViewActions from './actions';
import * as ActionsInternal from './actionsInternal';
import * as CustomRunNamesActions from './customRunNames/actions';
import * as FilterActions from './filter/actions';
import * as GroupSelectionsActions from './groupSelections/actions';
import * as GroupSelectionsUtils from './groupSelections/utils';
import {handleUpdateViewSpec} from './handleUpdateViewSpec';
import {markSectionPanelsAutoImmutable} from './markSectionPanelsAuto';
import * as Normalize from './normalize';
import * as PanelActions from './panel/actions';
import * as PanelBankConfigActions from './panelBankConfig/actions';
import * as PanelBankConfigActionsInternal from './panelBankConfig/actionsInternal';
import * as ClearPanelsUtils from './panelBankConfig/clearPanelsUtils';
import {movePanelAction} from './panelBankConfig/movePanelAction';
import {moveSectionAction} from './panelBankConfig/moveSectionAction';
import {undoMovePanelAction} from './panelBankConfig/undoMovePanelAction';
import * as PanelBankConfigUtils from './panelBankConfig/utils';
import * as PanelBankSectionConfigActions from './panelBankSectionConfig/actions';
import {type PanelConfigPatch} from './panelBankSectionConfig/actions';
import type * as PanelBankSectionConfigTypes from './panelBankSectionConfig/types';
import * as PanelBankSectionConfigUtils from './panelBankSectionConfig/utils';
import type {ActionType, ViewReducerState} from './reducerSupport';
import * as RunSetActions from './runSet/actions';
import type {RunSetPart} from './runSet/types';
import {
  bulkPinRuns,
  canPinRun,
  getGroupSelections,
  getRunSet,
  isRunPinned,
  setRunPinned,
  setRunPinnedAndBaseline,
  updateRunSet,
} from './runSet/utils';
import * as SectionActions from './section/actions';
import * as SectionUtils from './section/utils';
import * as TempSelectionsActions from './tempSelections/actions';
import {
  selectAll,
  selectAllVisible,
  selectNone,
  selectSome,
  selectToggle,
  updateTempSelectionsWithUndo,
} from './tempSelections/utils';
import {
  createPlacementOverridesForCustomPanels,
  maybeRemoveStaleConfigOverrideForPanel,
  swapConfigOverride,
  tryComputeSparseOverride,
} from './undoableOverrideUtils';
import * as WorkspaceSettingsActions from './workspaceSettings/actions';
import * as WorkspaceSettingsUtils from './workspaceSettings/utils';

const NO_OP_UNDO = ViewActions.noop();

function notUndoable(state: ViewReducerState): [ViewReducerState, ActionType] {
  return [state, NO_OP_UNDO];
}

const applyPanelUpdateConfigImmutable = (
  state: ViewReducerState,
  action: ReturnType<typeof PanelActions.updateConfig>,
  options: {makeInverse: boolean}
): [ViewReducerState, ActionType] => {
  const {ref, configUpdate, panelBankSectionConfigRef, panelBankConfigRef} =
    action.payload;
  const prevPanel = state.parts[ref.type][ref.id];
  const newPanelConfig = Object.assign({}, prevPanel.config, configUpdate);
  const section =
    panelBankSectionConfigRef == null
      ? undefined
      : state.parts[panelBankSectionConfigRef.type]?.[
          panelBankSectionConfigRef.id
        ];
  // Temporary editor copies are not section members; Apply handles sparse
  // overrides for the real panel, so staged edits should not do that work.
  const isDetachedPanelCopy =
    section != null &&
    !section.panelRefs.some(panelRef => panelRef.id === ref.id);

  // Try sparse config override path
  const sparseResult = isDetachedPanelCopy
    ? undefined
    : tryComputeSparseOverride(
        state,
        ref,
        newPanelConfig,
        panelBankConfigRef,
        panelBankSectionConfigRef
      );
  if (sparseResult) {
    // Update panel config but keep isAuto=true
    const newPanel = Object.assign({}, prevPanel, {
      config: newPanelConfig,
      key: sparseResult.overrideInfo.panelKey,
      isAuto: true,
    });
    const newState = setViewPartImmutable(
      sparseResult.updatedState,
      ref,
      newPanel
    );
    recordRumAction('sparse_overrides.config_edit', {
      usedSparseOverride: true,
      action: 'updateConfig',
    });
    return [
      newState,
      options.makeInverse
        ? PanelActions.setConfig(
            action.payload.ref,
            prevPanel.config,
            prevPanel.key,
            sparseResult.overrideInfo
          )
        : NO_OP_UNDO,
    ];
  }

  if (isSparseOverridesEnabled() && !isDetachedPanelCopy) {
    recordRumAction('sparse_overrides.config_edit', {
      usedSparseOverride: false,
      action: 'updateConfig',
    });
  }

  // Fallback to existing behavior for ineligible panels
  const isAuto = false;
  // Preserve key for panels whose identity is not tied to metric keys.
  // Clearing key is appropriate for default metric-bound panels (e.g.,
  // single-metric Run History Line Plot) so they become custom charts.
  // However, API-added panels like Weave Trace rely on a stable key
  // (e.g., 'weave-trace') for diff/dedup. Do not clear those keys.
  const preserveKeyViewTypes = new Set(['Weave Trace', 'Weave']);
  const newKey = preserveKeyViewTypes.has(prevPanel.viewType)
    ? prevPanel.key
    : undefined;
  const newPanel = Object.assign({}, prevPanel, {
    config: newPanelConfig,
    key: newKey,
    isAuto,
  });
  let newState = setViewPartImmutable(state, ref, newPanel);
  const cleanupResult =
    isSparseOverridesEnabled() && !isDetachedPanelCopy
      ? maybeRemoveStaleConfigOverrideForPanel(
          newState,
          panelBankConfigRef,
          prevPanel,
          ref
        )
      : {updatedState: newState, overrideInfo: undefined};
  newState = cleanupResult.updatedState;
  return [
    newState,
    options.makeInverse
      ? PanelActions.setConfig(
          action.payload.ref,
          prevPanel.config,
          prevPanel.key,
          cleanupResult.overrideInfo
        )
      : NO_OP_UNDO,
  ];
};

type PanelConfigRecord = Record<string, unknown>;

const hasOwn = (obj: PanelConfigRecord, key: string) =>
  Object.prototype.hasOwnProperty.call(obj, key);

const setViewPartImmutable = (
  state: ViewReducerState,
  ref: {type: keyof ViewReducerState['parts']; id: string},
  part: unknown
): ViewReducerState => {
  const bucket = state.parts[ref.type] as Record<string, unknown>;
  if (bucket[ref.id] === part) {
    return state;
  }

  // This hot path updates very large part maps during panel edit/apply. Avoid
  // object spread here; it was measurably slower in x-axis edit profiles.
  const nextBucket: Record<string, unknown> = {};
  const keys = Object.keys(bucket);
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    nextBucket[key] = bucket[key];
  }
  nextBucket[ref.id] = part;

  return {
    ...state,
    parts: {
      ...state.parts,
      [ref.type]: nextBucket,
    },
  } as ViewReducerState;
};

const setSectionIsPanelsAutoImmutable = (
  state: ViewReducerState,
  ref: PanelBankSectionConfigTypes.Ref,
  isPanelsAuto: boolean | undefined
): ViewReducerState => {
  const section = state.parts[ref.type]?.[ref.id];
  if (section?.isPanelsAuto === isPanelsAuto) {
    return state;
  }
  return setViewPartImmutable(state, ref, {
    ...section,
    isPanelsAuto,
  });
};

const makePanelConfigPatch = (
  fromConfig: PanelConfig | undefined,
  toConfig: PanelConfig | undefined
): PanelConfigPatch => {
  // Config updates are top-level immutable. Compare refs here so Apply does not
  // walk large nested config values just to build undo state.
  const from = (fromConfig ?? {}) as PanelConfigRecord;
  const to = (toConfig ?? {}) as PanelConfigRecord;
  const keys = new Set([...Object.keys(from), ...Object.keys(to)]);
  const values: PanelConfigPatch['values'] = {};
  const removedKeys: string[] = [];

  for (const key of keys) {
    const fromHasKey = hasOwn(from, key);
    const toHasKey = hasOwn(to, key);

    if (!toHasKey) {
      removedKeys.push(key);
    } else if (!fromHasKey || from[key] !== to[key]) {
      (values as PanelConfigRecord)[key] = to[key];
    }
  }

  return {values, removedKeys};
};

const applyPanelConfigPatch = (
  config: PanelConfig | undefined,
  patch: PanelConfigPatch
): PanelConfig => {
  const nextConfig = {
    ...((config ?? {}) as PanelConfigRecord),
    ...(patch.values as PanelConfigRecord),
  };
  for (const key of patch.removedKeys) {
    delete nextConfig[key];
  }
  return nextConfig as PanelConfig;
};

const sortPanelKey = (key: string | undefined) =>
  key === undefined ? '0' : '1' + key;

const maybeSortSectionPanelRefs = (
  state: ViewReducerState,
  sectionRef: PanelBankSectionConfigTypes.Ref
): ViewReducerState => {
  const section = state.parts[sectionRef.type]?.[sectionRef.id];
  if (section?.sorted !== SectionPanelSorting.Alphabetical) {
    return state;
  }

  const panelRefs = section.panelRefs;
  const sortedPanelRefs = [...panelRefs].sort((refA, refB) => {
    const keyA = panelSortingKeyFromPanel(state.parts[refA.type][refA.id]);
    const keyB = panelSortingKeyFromPanel(state.parts[refB.type][refB.id]);
    return sortPanelKey(keyA).localeCompare(sortPanelKey(keyB));
  });

  if (sortedPanelRefs.every((ref, index) => ref.id === panelRefs[index].id)) {
    return state;
  }

  return setViewPartImmutable(state, sectionRef, {
    ...section,
    panelRefs: sortedPanelRefs,
  });
};

const applyCopiedPanelConfigImmutable = (
  state: ViewReducerState,
  action: ReturnType<
    typeof PanelBankSectionConfigActions.applyCopiedPanelConfig
  >
): [ViewReducerState, ActionType] => {
  const {ref, fromPanelRef, panelRef, panelBankConfigRef} = action.payload;
  const editedPanel = state.parts[fromPanelRef.type][fromPanelRef.id];
  const prevPanel = state.parts[panelRef.type][panelRef.id];
  const prevIsPanelsAuto = state.parts[ref.type]?.[ref.id]?.isPanelsAuto;
  // Apply only the edited config subtree to the real panel. Replacing the whole
  // panel pulls unrelated view state into Immer and undo.
  const configPatch = makePanelConfigPatch(
    prevPanel.config,
    editedPanel.config
  );
  const nextConfig = applyPanelConfigPatch(prevPanel.config, configPatch);

  const sparseResult = tryComputeSparseOverride(
    state,
    panelRef,
    nextConfig,
    panelBankConfigRef,
    ref
  );
  const usesSparseOverride = sparseResult != null;
  const nextPanel = {
    ...prevPanel,
    config: nextConfig,
    key: usesSparseOverride
      ? sparseResult.overrideInfo.panelKey
      : editedPanel.key,
    isAuto: usesSparseOverride ? true : false,
  };

  let newState = setViewPartImmutable(
    sparseResult?.updatedState ?? state,
    panelRef,
    nextPanel
  );
  const cleanupResult =
    !usesSparseOverride && isSparseOverridesEnabled()
      ? maybeRemoveStaleConfigOverrideForPanel(
          newState,
          panelBankConfigRef,
          prevPanel,
          panelRef
        )
      : {updatedState: newState, overrideInfo: undefined};
  newState = cleanupResult.updatedState;
  newState = setSectionIsPanelsAutoImmutable(
    newState,
    ref,
    usesSparseOverride ? true : false
  );
  newState = maybeSortSectionPanelRefs(newState, ref);

  // Mirrors undoInsertUpdatedPanel's swap action, but stores only the config
  // patch so Apply doesn't put a full copied panel into undo history.
  const inverseAction =
    PanelBankSectionConfigActions.undoApplyCopiedPanelConfig(
      ref,
      panelRef,
      makePanelConfigPatch(nextConfig, prevPanel.config),
      prevPanel.key,
      prevPanel.isAuto,
      prevIsPanelsAuto,
      sparseResult?.overrideInfo ?? cleanupResult.overrideInfo
    );
  return [newState, inverseAction];
};

const undoApplyCopiedPanelConfigImmutable = (
  state: ViewReducerState,
  action: ReturnType<
    typeof PanelBankSectionConfigActions.undoApplyCopiedPanelConfig
  >
): [ViewReducerState, ActionType] => {
  const {ref, panelRef, configPatch, key, isAuto, isPanelsAuto, overrideInfo} =
    action.payload;
  const prevPanel = state.parts[panelRef.type][panelRef.id];
  const prevIsPanelsAuto = state.parts[ref.type]?.[ref.id]?.isPanelsAuto;
  const nextConfig = applyPanelConfigPatch(prevPanel.config, configPatch);

  let newState = state;
  let prevOverrideForInverse: typeof overrideInfo = undefined;
  if (overrideInfo) {
    const result = swapConfigOverride(newState, overrideInfo);
    newState = result.newState;
    prevOverrideForInverse = result.prevOverrideForInverse;
  }

  newState = setViewPartImmutable(newState, panelRef, {
    ...prevPanel,
    config: nextConfig,
    key,
    isAuto,
  });
  newState = setSectionIsPanelsAutoImmutable(newState, ref, isPanelsAuto);
  newState = maybeSortSectionPanelRefs(newState, ref);

  const inverseAction =
    PanelBankSectionConfigActions.undoApplyCopiedPanelConfig(
      ref,
      panelRef,
      makePanelConfigPatch(nextConfig, prevPanel.config),
      prevPanel.key,
      prevPanel.isAuto,
      prevIsPanelsAuto,
      prevOverrideForInverse
    );
  return [newState, inverseAction];
};

const applyAndMakeInverseActionImmutableInner = (
  state: ViewReducerState,
  action: ActionType
): [ViewReducerState, ActionType] => {
  switch (action.type) {
    case getType(ActionsInternal.undoableUpdateViewSpec): {
      const {ref, prevSpec, newSpec} = action.payload;
      const newState = handleUpdateViewSpec(state, ref.id, newSpec);
      const inverseAction = ActionsInternal.undoableUpdateViewSpec(ref, {
        prevSpec: newSpec, //  Note how the specs are swapped for undo action
        newSpec: prevSpec,
      });
      return [newState, inverseAction];
    }

    case getType(CustomRunNamesActions.setCustomRunName): {
      const {ref, id, name} = action.payload;
      const newState = setInShallowClone(
        state,
        ['parts', ref.type, ref.id, id],
        name || undefined // || undefined ensures we don't keep unnecessary empty string entries
      );
      const inverseAction = CustomRunNamesActions.setCustomRunName(
        ref,
        id,
        state.parts[ref.type][ref.id][id]
      );
      return [newState, inverseAction];
    }

    case getType(GroupSelectionsActions.toggleSelection): {
      const {ref, run, depth} = action.payload;
      return GroupSelectionsUtils.toggleSelection(state, ref, run, depth);
    }

    case getType(GroupSelectionsActions.toggleExpandedRowAddress): {
      const {ref, rowAddress} = action.payload;
      return GroupSelectionsUtils.toggleExpandedRowAddress(
        state,
        ref,
        rowAddress
      );
    }

    case getType(GroupSelectionsActions.setGrouping): {
      const {ref, grouping} = action.payload;
      return GroupSelectionsUtils.setGrouping(state, ref, grouping);
    }

    case getType(PanelActions.setConfig): {
      const {
        ref,
        config,
        key,
        overrideInfo,
        panelBankSectionConfigRef,
        panelBankConfigRef,
      } = action.payload;
      const prevPanel = state.parts[ref.type][ref.id];
      const newPanel = Object.assign({}, prevPanel, {config, key});
      let newState = setInShallowClone(
        state,
        ['parts', ref.type, ref.id],
        newPanel
      );

      // Capture the current isPanelsAuto so the inverse can restore it.
      // markSectionPanelsAutoImmutable runs after this and may change it.
      const prevIsPanelsAuto = panelBankSectionConfigRef
        ? state.parts[panelBankSectionConfigRef.type]?.[
            panelBankSectionConfigRef.id
          ]?.isPanelsAuto
        : undefined;

      // Restore override if provided (for sparse override undo support)
      let prevOverrideForInverse: typeof overrideInfo = undefined;
      if (overrideInfo) {
        const result = swapConfigOverride(newState, overrideInfo);
        newState = result.newState;
        prevOverrideForInverse = result.prevOverrideForInverse;
      } else if (panelBankConfigRef != null) {
        // Forward action with sparse override support (e.g., unsetConfigField)
        const sparseResult = tryComputeSparseOverride(
          state,
          ref,
          config,
          panelBankConfigRef,
          panelBankSectionConfigRef
        );
        if (sparseResult) {
          // Keep panel as auto
          const resultState = setInShallowClone(
            sparseResult.updatedState,
            ['parts', ref.type, ref.id],
            {
              ...newPanel,
              key: sparseResult.overrideInfo.panelKey,
              isAuto: true,
            }
          );
          const inverseAction = PanelActions.setConfig(
            ref,
            prevPanel.config,
            prevPanel.key,
            sparseResult.overrideInfo
          );
          recordRumAction('sparse_overrides.config_edit', {
            usedSparseOverride: true,
            action: 'setConfig',
          });
          return [resultState, inverseAction];
        }
        if (isSparseOverridesEnabled()) {
          recordRumAction('sparse_overrides.config_edit', {
            usedSparseOverride: false,
            action: 'setConfig',
          });
        }
        if (isSparseOverridesEnabled()) {
          const cleanupResult = maybeRemoveStaleConfigOverrideForPanel(
            newState,
            panelBankConfigRef,
            prevPanel,
            ref
          );
          newState = cleanupResult.updatedState;
          prevOverrideForInverse = cleanupResult.overrideInfo;
        }
      }

      const inverseAction = PanelActions.setConfig(
        action.payload.ref,
        prevPanel.config,
        prevPanel.key,
        prevOverrideForInverse,
        panelBankSectionConfigRef,
        panelBankConfigRef,
        panelBankSectionConfigRef ? {value: prevIsPanelsAuto} : undefined
      );
      return [newState, inverseAction];
    }

    case getType(PanelActions.updateConfig): {
      return applyPanelUpdateConfigImmutable(state, action, {
        makeInverse: true,
      });
    }

    case getType(PanelBankSectionConfigActions.applyCopiedPanelConfig): {
      return applyCopiedPanelConfigImmutable(state, action);
    }

    case getType(PanelBankSectionConfigActions.undoApplyCopiedPanelConfig): {
      return undoApplyCopiedPanelConfigImmutable(state, action);
    }

    case getType(PanelBankConfigActions.addPanelsBySpec): {
      const {ref, specs, shouldAlphaInsert} = action.payload;
      return PanelBankConfigUtils.addPanelsBySpec(
        state,
        ref,
        specs,
        shouldAlphaInsert
      );
    }

    case getType(PanelBankConfigActions.addPanelsBySpecUndo): {
      const {ref, sectionsMetadata} = action.payload;
      return PanelBankConfigUtils.addPanelsBySpecUndo(
        state,
        ref,
        sectionsMetadata
      );
    }

    case getType(PanelBankConfigActions.clearAllPanels): {
      const {ref, workspaceSettingsRef} = action.payload;
      return ClearPanelsUtils.clearAllPanels(state, ref, workspaceSettingsRef);
    }

    case getType(PanelBankConfigActions.clearAllPanelsUndo): {
      const {ref, workspaceSettingsRef, sectionRefs} = action.payload;
      return ClearPanelsUtils.clearAllPanelsUndo(
        state,
        ref,
        workspaceSettingsRef,
        sectionRefs
      );
    }

    case getType(PanelBankConfigActions.moveSection): {
      const {ref, activeSectionRefId, targetSectionRefId} = action.payload;
      return moveSectionAction(
        state,
        ref,
        activeSectionRefId,
        targetSectionRefId
      );
    }

    case getType(PanelBankConfigActions.movePanel): {
      const {ref, active, target} = action.payload;
      return movePanelAction(state, ref, active, target);
    }

    case getType(PanelBankConfigActions.undoMovePanel): {
      const {ref, active, target} = action.payload;
      return undoMovePanelAction(state, ref, active, target);
    }

    case getType(PanelBankConfigActions.openOrCloseAllSections): {
      const {ref, isOpen} = action.payload;
      return PanelBankConfigUtils.updateSections(
        state,
        ref,
        curSectionConfig => {
          if (curSectionConfig.isOpen === isOpen) {
            return curSectionConfig;
          }
          return Object.assign({}, curSectionConfig, {isOpen});
        }
      );
    }

    case getType(PanelBankConfigActions.updateAllLinePlotSectionSettings): {
      const {ref, settings} = action.payload;
      return PanelBankConfigUtils.updateAllLinePlotSectionSettings(
        state,
        ref,
        settings
      );
    }

    case getType(PanelBankConfigActionsInternal.setAllSections): {
      const {ref, newSectionsNormalized} = action.payload;
      return PanelBankConfigUtils.updateSections(
        state,
        ref,
        (curSectionConfig, i) => newSectionsNormalized[i]
      );
    }

    case getType(PanelBankSectionConfigActions.addPanelByConfig): {
      const {
        ref,
        panel,
        shouldAlphaInsert,
        fatPanel,
        callbackFn,
        panelBankConfigRef: pbcRef,
      } = action.payload;
      const {newState, newPanelRefs, prevIsPanelsAuto} =
        PanelBankSectionConfigUtils.addPanels(
          state,
          ref,
          [panel],
          shouldAlphaInsert,
          fatPanel,
          callbackFn
        );
      const stateWithOverrides = createPlacementOverridesForCustomPanels(
        newState,
        ref,
        newPanelRefs,
        pbcRef
      );
      const inverseAction = PanelBankSectionConfigActions.deletePanel(
        ref,
        newPanelRefs[0],
        undefined,
        undefined,
        {value: prevIsPanelsAuto}
      );
      return [stateWithOverrides, inverseAction];
    }

    case getType(PanelBankSectionConfigActions.addPanelByRef): {
      const {
        ref,
        panelRef,
        shouldAlphaInsert,
        fatPanel,
        callbackFn,
        panelBankConfigRef: pbcRef,
      } = action.payload;
      const panel = Normalize.denormalize(state.parts, panelRef);
      const {newState, newPanelRefs, prevIsPanelsAuto} =
        PanelBankSectionConfigUtils.addPanels(
          state,
          ref,
          [panel],
          shouldAlphaInsert,
          fatPanel,
          callbackFn
        );
      const stateWithOverrides = createPlacementOverridesForCustomPanels(
        newState,
        ref,
        newPanelRefs,
        pbcRef
      );
      const inverseAction = PanelBankSectionConfigActions.deletePanel(
        ref,
        newPanelRefs[0],
        undefined,
        undefined,
        {value: prevIsPanelsAuto}
      );
      return [stateWithOverrides, inverseAction];
    }

    case getType(PanelBankSectionConfigActions.addPanelsByConfig): {
      const {
        ref,
        panels,
        shouldAlphaInsert,
        panelBankConfigRef: pbcRef,
      } = action.payload;
      const {newState, newPanelRefs, prevIsPanelsAuto} =
        PanelBankSectionConfigUtils.addPanels(
          state,
          ref,
          panels,
          shouldAlphaInsert
        );

      // Create placement overrides for custom panels
      const stateWithOverrides = createPlacementOverridesForCustomPanels(
        newState,
        ref,
        newPanelRefs,
        pbcRef
      );

      const inverseAction = PanelActions.deletePanels(
        newPanelRefs,
        ref,
        undefined,
        undefined,
        {value: prevIsPanelsAuto}
      );
      return [stateWithOverrides, inverseAction];
    }

    case getType(PanelBankSectionConfigActions.toggleIsOpen): {
      const {ref} = action.payload;
      return PanelBankSectionConfigUtils.toggleIsOpen(state, ref);
    }

    case getType(PanelBankSectionConfigActions.updateFlowConfig): {
      const {ref, newFlowConfig, operation} = action.payload;
      const prevFlowConfig = state.parts[ref.type][ref.id].flowConfig;
      const newState = setInShallowClone(
        state,
        ['parts', ref.type, ref.id, 'flowConfig'],
        operation === 'PATCH'
          ? {...prevFlowConfig, ...newFlowConfig}
          : newFlowConfig
      );
      return [
        newState,
        PanelBankSectionConfigActions.updateFlowConfig(
          ref,
          prevFlowConfig,
          'PUT'
        ),
      ];
    }

    case getType(PanelBankSectionConfigActions.updateLinePlotSectionSettings): {
      const {ref, settings} = action.payload;
      return PanelBankSectionConfigUtils.updateLinePlotSectionSettings(
        state,
        ref,
        settings
      );
    }

    case getType(PanelBankSectionConfigActions.updateSectionMediaSettings): {
      const {ref, settings} = action.payload;
      return PanelBankSectionConfigUtils.updateSectionMediaSettings(
        state,
        ref,
        settings
      );
    }

    case getType(PanelBankSectionConfigActions.unsetSectionMediaSetting): {
      const {ref, field} = action.payload;
      return PanelBankSectionConfigUtils.unsetSectionMediaSetting(
        state,
        ref,
        field
      );
    }
    case getType(PanelBankSectionConfigActions.updateName): {
      const {ref, newName, panelAutoVals} = action.payload;
      return PanelBankSectionConfigUtils.updateName(
        state,
        ref,
        newName,
        panelAutoVals
      );
    }

    case getType(RunSetActions.update): {
      const {ref, runSetUpdate} = action.payload;
      const runSet = getRunSet(state, ref);
      if (!runSet) {
        return notUndoable(state);
      }
      const updater =
        typeof runSetUpdate === 'function'
          ? runSetUpdate
          : (runSet: RunSetPart) => ({...runSet, ...runSetUpdate});

      const newState = updateRunSet(state, ref, updater);

      if (newState === state) {
        // No change was made to the runset, so do nothing.
        return notUndoable(state);
      }

      const inverseAction = RunSetActions.set(ref, runSet);
      return [newState, inverseAction];
    }

    case getType(RunSetActions.setPinnedRun): {
      const {ref, run, pinned} = action.payload;
      const runSet = getRunSet(state, ref);
      if (!runSet) {
        return notUndoable(state);
      }

      if (pinned && !canPinRun(runSet)) {
        // Can't pin run for some reason (e.g. too many runs already pinned)
        return notUndoable(state);
      }

      const newState = updateRunSet(state, ref, runSet =>
        setRunPinned(runSet, run, pinned)
      );

      return [newState, RunSetActions.setPinnedRun(ref, run, !pinned)];
    }
    case getType(RunSetActions.setBulkPinnedRuns): {
      const {ref, runIds} = action.payload;
      const runSet = getRunSet(state, ref);
      if (!runSet) {
        return notUndoable(state);
      }
      const newState = updateRunSet(state, ref, rs => bulkPinRuns(rs, runIds));
      if (newState === state) {
        return notUndoable(state);
      }
      return [newState, RunSetActions.set(ref, runSet)];
    }
    case getType(RunSetActions.setBaselineRun): {
      const {ref, run} = action.payload;
      const runSet = getRunSet(state, ref);
      if (!runSet) {
        return notUndoable(state);
      }

      if (run && !canPinRun(runSet) && !isRunPinned(runSet.pinnedRunIds, run)) {
        // Can't pin run for some reason (e.g. too many runs already pinned),
        // which means we can't set a baseline either.
        return notUndoable(state);
      }

      const currentBaselineRun = runSet.baselineRunId;
      const newState = updateRunSet(state, ref, runSet =>
        setRunPinnedAndBaseline(runSet, run)
      );

      return [
        newState,
        RunSetActions.setBaselineRun(
          ref,
          currentBaselineRun ? {id: currentBaselineRun} : null
        ),
      ];
    }

    case getType(WorkspaceSettingsActions.updateAutoOrganizePrefix): {
      const {workspaceSettingsRef, autoOrganizePrefix, panelBankConfigRef} =
        action.payload;
      return WorkspaceSettingsUtils.updateAutoOrganizePrefix(
        state,
        workspaceSettingsRef,
        panelBankConfigRef,
        autoOrganizePrefix
      );
    }

    case getType(WorkspaceSettingsActions.updateAutoOrganizePrefixUndo): {
      const {
        workspaceSettingsRef,
        autoOrganizePrefix,
        panelBankConfigRef,
        sectionRefs,
      } = action.payload;
      return WorkspaceSettingsUtils.updateAutoOrganizePrefixUndo(
        state,
        workspaceSettingsRef,
        panelBankConfigRef,
        sectionRefs,
        autoOrganizePrefix
      );
    }

    case getType(WorkspaceSettingsActions.updateLinePlotWorkspaceSettings): {
      const {ref, settings} = action.payload;
      return WorkspaceSettingsUtils.updateLinePlotWorkspaceSettings(
        state,
        ref,
        settings
      );
    }

    case getType(WorkspaceSettingsActions.updateWorkspaceMediaSettings): {
      const {ref, settings} = action.payload;
      return WorkspaceSettingsUtils.updateWorkspaceMediaSettings(
        state,
        ref,
        settings
      );
    }

    case getType(WorkspaceSettingsActions.unsetWorkspaceMediaSetting): {
      const {ref, key} = action.payload;
      return WorkspaceSettingsUtils.unsetWorkspaceMediaSetting(state, ref, key);
    }

    case getType(
      WorkspaceSettingsActions.updateSemanticLegendRunColorOptions
    ): {
      const {ref, semanticLegendRunColorOptions} = action.payload;
      return WorkspaceSettingsUtils.updateSemanticLegendRunColorOptions(
        state,
        ref,
        semanticLegendRunColorOptions
      );
    }

    case getType(
      WorkspaceSettingsActions.updateSemanticLegendRunColorOptionsAndClearCustomRunColors
    ): {
      const {ref, customRunColorsRef, semanticLegendRunColorOptions} =
        action.payload;
      return WorkspaceSettingsUtils.updateSemanticLegendRunColorOptionsAndClearCustomRunColors(
        state,
        ref,
        customRunColorsRef,
        semanticLegendRunColorOptions
      );
    }

    case getType(
      WorkspaceSettingsActions.updateSemanticLegendRunColorOptionsAndClearCustomRunColorsUndo
    ): {
      const {
        ref,
        customRunColorsRef,
        semanticLegendRunColorOptions,
        customRunColors,
      } = action.payload;
      return WorkspaceSettingsUtils.updateSemanticLegendRunColorOptionsAndClearCustomRunColorsUndo(
        state,
        ref,
        customRunColorsRef,
        semanticLegendRunColorOptions,
        customRunColors
      );
    }

    case getType(WorkspaceSettingsActions.updateShouldAutoGeneratePanels): {
      const {workspaceSettingsRef, panelBankConfigRef, value} = action.payload;
      return WorkspaceSettingsUtils.updateShouldAutoGeneratePanels(
        state,
        workspaceSettingsRef,
        panelBankConfigRef,
        value
      );
    }

    case getType(WorkspaceSettingsActions.updateColorPaletteOption): {
      const {ref, newColorPalette} = action.payload;
      return WorkspaceSettingsUtils.updateColorPaletteOption(
        state,
        ref,
        newColorPalette
      );
    }

    case getType(WorkspaceSettingsActions.updateSemanticLegendPalette): {
      const {ref, palette} = action.payload;
      return WorkspaceSettingsUtils.updateSemanticLegendPalette(
        state,
        ref,
        palette
      );
    }
    case getType(WorkspaceSettingsActions.setWorkspaceSettings): {
      const {ref, settings} = action.payload;
      return WorkspaceSettingsUtils.setWorkspaceSettings(state, ref, settings);
    }

    case getType(WorkspaceSettingsActions.updateWorkspaceLayoutSettings): {
      const {ref, settings} = action.payload;
      return WorkspaceSettingsUtils.updateWorkspaceLayoutSettings(
        state,
        ref,
        settings
      );
    }

    case getType(PanelBankConfigActions.updateSettings): {
      const {ref, panelBankSettings} = action.payload;
      return PanelBankConfigUtils.updateSettings(state, ref, panelBankSettings);
    }

    // Keep this separate from updateSettings because published workspaces allow
    // search updates while rejecting other settings changes.
    case getType(PanelBankConfigActions.setCurrentPanelSearch): {
      const {hideSearchQuery, ref, searchQuery} = action.payload;
      const prevSettings = state.parts[ref.type][ref.id].settings;
      const hasPanelSearchFilter = !!searchQuery || !!hideSearchQuery;
      return PanelBankConfigUtils.updateSettings(state, ref, {
        hideSearchQuery,
        searchQuery,
        // Clear temporary section state only after both filters are cleared.
        searchSectionsOpen: hasPanelSearchFilter
          ? prevSettings.searchSectionsOpen
          : undefined,
      });
    }

    case getType(SectionActions.addNewRunSet): {
      return SectionUtils.addNewRunSet(state, action.payload.ref);
    }

    case getType(FilterActions.set): {
      const {ref, filters} = action.payload;
      const prevFilters = state.parts.filters[ref.id];
      const newState = setInShallowClone(
        state,
        ['parts', 'filters', ref.id],
        filters
      );
      return [newState, FilterActions.set(action.payload.ref, prevFilters)];
    }

    case getType(TempSelectionsActions.selectAllVisible): {
      const {ref, runSetRef} = action.payload;

      return updateTempSelectionsWithUndo(state, ref, tempSelection =>
        selectAllVisible(
          tempSelection,
          getRunSet(state, runSetRef),
          getGroupSelections(state, runSetRef)
        )
      );
    }

    case getType(TempSelectionsActions.selectNone): {
      const {ref, runSetRef} = action.payload;

      return updateTempSelectionsWithUndo(state, ref, tempSelection =>
        selectNone(tempSelection, getGroupSelections(state, runSetRef))
      );
    }

    case getType(TempSelectionsActions.selectToggle): {
      const {ref, run, runSetRef, depth} = action.payload;

      return updateTempSelectionsWithUndo(state, ref, tempSelection =>
        selectToggle(
          tempSelection,
          getGroupSelections(state, runSetRef),
          run,
          depth
        )
      );
    }

    case getType(TempSelectionsActions.selectSome): {
      const {ref, runs, runSetRef} = action.payload;

      return updateTempSelectionsWithUndo(state, ref, tempSelection =>
        selectSome(tempSelection, getGroupSelections(state, runSetRef), runs)
      );
    }

    case getType(TempSelectionsActions.selectAll): {
      const {ref, runSetRef} = action.payload;

      return updateTempSelectionsWithUndo(state, ref, tempSelection =>
        selectAll(
          tempSelection,
          getRunSet(state, runSetRef),
          getGroupSelections(state, runSetRef)
        )
      );
    }
  }
  throw new Error('Action not undoable');
};

export const applyAndMakeInverseActionImmutable = (
  state: ViewReducerState,
  action: ActionType
): [ViewReducerState, ActionType] => {
  const [stateAfterApplyingAction, inverseAction] =
    applyAndMakeInverseActionImmutableInner(state, action);
  const stateAfterMarkingIsPanelsAuto = markSectionPanelsAutoImmutable(
    stateAfterApplyingAction,
    action,
    inverseAction
  );
  return [stateAfterMarkingIsPanelsAuto, inverseAction];
};

export const applyUndoableActionImmutable = (
  state: ViewReducerState,
  action: ActionType
) => {
  // Check if action should be undoable before computing inverse
  const payload = (action as any).payload;
  if (payload?.options?.isUndoable === false) {
    if (action.type === getType(PanelActions.updateConfig)) {
      const [newState] = applyPanelUpdateConfigImmutable(state, action, {
        makeInverse: false,
      });
      return markSectionPanelsAutoImmutable(newState, action, NO_OP_UNDO);
    }

    // Apply action but don't track undo/redo. If a new action adds support for
    // `isUndoable: false`, give it an explicit no-inverse path above.
    const [newState] = applyAndMakeInverseActionImmutable(state, action);
    return newState;
  }

  // Normal undoable path
  const [newState, inverseAction] = applyAndMakeInverseActionImmutable(
    state,
    action
  );

  if (newState === state) {
    // Reducer responded with a reference-identical value, so no state update
    // was made. Don't add to undo stack needlessly.
    return state;
  }

  // Reducer branch responded that undoing is a no-op, so don't add to undo
  // stack needlessly.
  if (inverseAction === NO_OP_UNDO) {
    return newState;
  }

  return Object.assign(newState, {
    undoActions: state.undoActions.concat({id: ID(), action: inverseAction}),
    redoActions: [],
  });
};
